import { Component, effect, inject, input, output } from '@angular/core'
import { MatMenuModule } from '@angular/material/menu'
import { IconsModule } from '@ziptility/icons'
import { BoardsStore, type SwimLane, type TaskBoardFollower } from '../../boards/boards.store'
import { firstValueFrom } from 'rxjs'
import { AuthorizationService } from '@ziptility/services'
import { FormsModule } from '@angular/forms'
import { AddTaskBoardFollowerGQL, RemoveTaskBoardFollowerGQL, TaskBoardType, User } from '@ziptility/graphql'
import { BvAlertService } from '@ziptility/bv-alert'
import { first } from 'lodash/fp'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { saveHiddenBoards } from '@ziptility/store/preference/actions'
import { Store } from '@ngrx/store'
import { UserAvatarComponent } from '@ziptility/components/user-avatar'
import { getUser } from '@ziptility/store'
import { toSignal } from '@angular/core/rxjs-interop'

@Component({
  selector: 'swimlane-header',
  template: `
    <div class="px-2 py-2 font-semibold flex items-center justify-between space-x-2 border-b border-gray-300 ">
      <div class="flex items-center space-x-2">
        @if (swimLane().board.type == TaskBoardType.User) {
          <user-avatar [fullName]="swimLane().board.name" [displayStyle]="'initials'"></user-avatar>
        }
        <div>
          {{ swimLane().board.name }}
        </div>
        @if (swimLane().tasks.length > 0) {
          <div>({{ swimLane().totalCount }})</div>
        }
        @if (swimLane().loading) {
          <div class="flex items-center justify-center">
            <mat-spinner [diameter]="20" class="gray"></mat-spinner>
          </div>
        }
      </div>
      <div class="flex items-center space-x-2">
        <div class="flex items-center justify-center" [matMenuTriggerFor]="swimlaneOptions">
          <i-feather name="more-horizontal" class="feather-icon-sm"></i-feather>
        </div>
        @if (collapsible()) {
          <button class="flex items-center justify-center">
            <i-feather
              [name]="collapsed() ? 'chevron-up' : 'chevron-down'"
              class="feather-icon-sm"
              (click)="onCollapse.emit()"
            ></i-feather>
          </button>
        }
      </div>

      <mat-menu #swimlaneOptions>
        <div class="">
          <div class="p-2 bv-menu">
            <div class="px-4 text-xs text-gray-500">{{ swimLane().board.name }}</div>
            <div class="bv-menu-item flex items-center space-x-2" (click)="onHide()">
              <i-feather name="eye" class="feather-icon-sm"></i-feather>
              <div>Hide</div>
            </div>
            @let user = this.user();
            @if (swimLane().board.type == TaskBoardType.TaskList) {
              @if (user?.id; as userId) {
              @if (!userIsFollowing) {
                <div
                  class="bv-menu-item flex items-center space-x-2"
                  (click)="$event.stopPropagation(); follow(userId, swimLane().board.taskListId)"
                >
                  <i-feather name="bell" class="feather-icon-sm"></i-feather>
                  <div>Follow</div>
                </div>
              } @else {
                <div
                  class="bv-menu-item flex items-center space-x-2"
                  (click)="$event.stopPropagation(); unfollow(userId, swimLane().board.taskListId)"
                >
                  <i-feather name="bell-off" class="feather-icon-sm"></i-feather>
                  <div>Unfollow</div>
                </div>
              }
            }
              @if (canManageFollowers()) {
                <div
                  class="bv-menu-item flex items-center space-x-2"
                  (click)="$event.stopPropagation()"
                  [mat-menu-trigger-for]="followers"
                >
                  <i-feather name="user" class="feather-icon-sm"></i-feather>
                  <div>Manage Followers</div>
                  <mat-menu #followers>
                    <div class="bv-menu" (click)="$event.stopPropagation()">
                      @for (follower of boardFollowers; track follower.id) {
                        <div class="bv-menu-item flex items-center space-x-2">
                          <label class="flex items-center">
                            <input
                              type="checkbox"
                              class="mr-1"
                              [(ngModel)]="follower.following"
                              (ngModelChange)="
                                $event ? follow(follower.id, swimLane().board.taskListId) : unfollow(follower.id, swimLane().board.taskListId)
                              "
                            />
                            {{ follower.name }}
                          </label>
                        </div>
                      }
                    </div>
                  </mat-menu>
                </div>
              }
              <button class="bv-menu-item flex items-center space-x-2" (click)="onRename.emit()">Rename</button>
              <div class="bv-menu-item flex items-center space-x-2" (click)="onDelete.emit()">Delete</div>
            }
          </div>
        </div>
      </mat-menu>
    </div>
  `,
  imports: [FormsModule, UserAvatarComponent, MatMenuModule, IconsModule, MatProgressSpinnerModule],
})
export class SwimLaneHeaderComponent {
  TaskBoardType = TaskBoardType
  readonly store = inject(Store)
  readonly taskBoardStore = inject(BoardsStore)
  readonly authService = inject(AuthorizationService)
  readonly addBoardFollower = inject(AddTaskBoardFollowerGQL)
  readonly removeBoardFollower = inject(RemoveTaskBoardFollowerGQL)
  readonly alertService = inject(BvAlertService)
  readonly user = toSignal(this.store.select(getUser))

  readonly swimLane = input.required<SwimLane>()
  readonly collapsible = input(false)
  readonly collapsed = input(false)
  readonly canManageFollowers = input(this.authService.canManageAllTasks())
  readonly followerOptions = input<User[]>()

  boardFollowers: TaskBoardFollower[] = []
  readonly onRename = output<void>()
  readonly onDelete = output<void>()
  readonly onCollapse = output<void>()

  get userIsFollowing() {
    return !this.user()
      ? false
      : this.boardFollowers.find((f) => f.id == this.user()?.id && f.following == true) != undefined
  }

  constructor() {
    effect(() => {
      this.boardFollowers = this.makeFollowerList(this.swimLane(), this.followerOptions())
    })
  }

  onHide() {
    this.taskBoardStore.toggleSwimLaneVisibility({
      swimLaneId: this.swimLane().id,
      visible: false,
    })

    this.store.dispatch(saveHiddenBoards({ hiddenBoards: this.taskBoardStore.hiddenSwimLaneIds() }))
  }

  makeFollowerList(sl: SwimLane | undefined, options: User[] | undefined): TaskBoardFollower[] {
    if (!sl || !options) {
      return []
    }
    const followerMap = sl.board.followers.reduce<Record<string, User>>((obj, f: User) => {
      obj[f.id] = f
      return obj
    }, {})

    const following = options.reduce<Record<string, boolean>>((obj, u: User) => {
      obj[u.id] = !!followerMap[u.id]
      return obj
    }, {})
    return options.map(
      (u: User): TaskBoardFollower => ({
        id: u.id,
        name: u.fullName ?? '',
        following: following[u.id],
      }),
    )
  }

  async follow(followerId: string, listId: string) {
    const user = this.boardFollowers.find((f) => f.id == followerId)
    if (!user) {
      return
    }
    user.following = true
    try {
      const res = await firstValueFrom(
        this.addBoardFollower.mutate({
          request: {
            followerId,
            taskBoardId: listId,
          },
        }),
      )

      const data = res.data
      if (!data) {
        user.following = false
        return
      }
      const {
        addTaskBoardFollower: { errors },
      } = data
      if (errors && errors.length) {
        this.alertService.error(first(errors)?.message)
        user.following = false
        return
      }
    } catch (error) {
      user.following = false
    }
  }

  async unfollow(followerId: string, listId: string) {
    const user = this.boardFollowers.find((f) => f.id == followerId)
    if (!user) {
      return
    }

    user.following = false
    try {
      const res = await firstValueFrom(
        this.removeBoardFollower.mutate({
          request: {
            followerId,
            taskBoardId: listId,
          },
        }),
      )

      const data = res.data
      if (!data) {
        user.following = true
        return
      }
      const {
        removeTaskBoardFollower: { errors },
      } = data
      if (errors && errors.length) {
        this.alertService.error(first(errors)?.message)
        user.following = true
        return
      }
    } catch (error) {
      user.following = true
    }
  }
}
